// src/pages/OrderOnTheWay/components/PaymentDeliveredList.jsx
import React, { useMemo, useState } from "react";
import {
  calculateJmiSettlement,
  calculateFourPartyPricing,
} from "../utils/calculationutil";
import JmiCommissionPayModal from "./JmiCommissionPayModal";
import FourPartyPricingModal from "./FourPartyPricingModal";
import {
  CheckCircle2,
  Clock,
  Landmark,
  ShieldCheck,
  Filter,
  Calculator,
  ChevronDown,
  ChevronRight,
  User,
  Store,
  UserCheck,
} from "lucide-react";

// Table styles
const tableHeaderStyle = {
  border: "1px solid #1f2937",
  padding: "6px 8px",
  textAlign: "left",
  fontSize: "11px",
  fontWeight: "bold",
  backgroundColor: "#f3f4f6",
  color: "#374151",
  whiteSpace: "nowrap",
};

const tableCellStyle = {
  border: "1px solid #1f2937",
  padding: "6px 8px",
  fontSize: "12px",
  verticalAlign: "middle",
};

const PaymentDeliveredList = ({
  groupedOrders,
  loading,
  setAlertMessage,
  rtgsSaleRate = 0,
  onOpenJmiPayModal,
}) => {
  const [expandedSellers, setExpandedSellers] = useState({});
  // Header-level JMI clearance split (the old filter buttons are gone).
  const jmiClearance = useMemo(() => {
    let cleared = 0;
    let due = 0;
    Object.values(groupedOrders || {}).forEach((group) => {
      (group?.orders || []).forEach((order) => {
        if (order.jmiSettled || order.jmiCommissionSettled) cleared += 1;
        else due += 1;
      });
    });
    return { cleared, due };
  }, [groupedOrders]);
  const [modalOrders, setModalOrders] = useState(null);
  const [selectedPricingOrder, setSelectedPricingOrder] = useState(null);

  const toggleSeller = (sellerKey) => {
    setExpandedSellers((prev) => ({
      ...prev,
      [sellerKey]: !prev[sellerKey],
    }));
  };

  const formatDate = (date) => {
    if (!date) return "—";
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleString("en-IN");
    } catch {
      return "—";
    }
  };

  const getOrderStatusBadge = (status) => {
    const isSettled = status === "PaymentSettled" || status === "paymentSettled";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "3px 8px",
          fontSize: "11px",
          fontWeight: "bold",
          borderRadius: "4px",
          backgroundColor: isSettled ? "#dcfce7" : "#d1fae5",
          color: isSettled ? "#15803d" : "#065f46",
          border: `1px solid ${isSettled ? "#86efac" : "#a7f3d0"}`,
        }}
      >
        <CheckCircle2 size={12} />
        {isSettled ? "PaymentSettled" : status || "Delivered"}
      </span>
    );
  };

  const calculateTotals = (orders) => {
    const jmi = calculateJmiSettlement(orders);
    const jmiFineAmt = jmi.fine * (parseFloat(rtgsSaleRate) || 0);
    const totalJmiComm = jmiFineAmt + jmi.amount;

    const base = orders.reduce(
      (acc, order) => {
        const netWt = parseFloat(order.netWt) || 0;
        const grossWt = parseFloat(order.grossWt) || 0;
        const amount = parseFloat(order.totalAmount || order.totalPaymentAmount) || 0;
        const qty = parseFloat(order.totalQty) || 0;
        const isJmiCleared = Boolean(order.jmiSettled || order.jmiCommissionSettled);

        return {
          totalOrders: acc.totalOrders + 1,
          totalNetWt: acc.totalNetWt + netWt,
          totalGrossWt: acc.totalGrossWt + grossWt,
          totalAmount: acc.totalAmount + amount,
          totalQty: acc.totalQty + qty,
          jmiClearedCount: acc.jmiClearedCount + (isJmiCleared ? 1 : 0),
          jmiPendingCount: acc.jmiPendingCount + (isJmiCleared ? 0 : 1),
        };
      },
      {
        totalOrders: 0,
        totalNetWt: 0,
        totalGrossWt: 0,
        totalAmount: 0,
        totalQty: 0,
        jmiClearedCount: 0,
        jmiPendingCount: 0,
      }
    );

    return {
      ...base,
      jmiFine: jmi.fine,
      jmiAmount: jmi.amount,
      jmiFineAmt,
      totalJmiComm,
    };
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "40px", fontSize: "16px", color: "#6b7280" }}>
        Loading delivered orders...
      </div>
    );
  }

  if (Object.keys(groupedOrders).length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px", fontSize: "16px", color: "#6b7280" }}>
        No delivered / PaymentSettled orders.
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 0", overflowX: "auto" }}>
      {/* Filter Toolbar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "14px",
          flexWrap: "wrap",
          gap: "10px",
          backgroundColor: "#ffffff",
          padding: "10px 16px",
          borderRadius: "8px",
          border: "1px solid #e5e7eb",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Filter size={16} color="#4b5563" />
          <span style={{ fontSize: "13px", fontWeight: "600", color: "#374151" }}>
            JMI clearance:
          </span>
          {/* The all/pending/cleared filter buttons were removed — the
              Settlement Reconciliation dashboard above now carries the cleared
              vs due state per order (and per settlement). */}
          <span style={{ fontSize: "12px", color: "#4b5563" }}>
            {jmiClearance.due} due · {jmiClearance.cleared} cleared
          </span>
        </div>

        {onOpenJmiPayModal && (
          <button
            onClick={onOpenJmiPayModal}
            style={{
              backgroundColor: "#4f46e5",
              color: "#fff",
              border: "none",
              padding: "7px 14px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <ShieldCheck size={14} /> Settle JMI Service-fee
          </button>
        )}
      </div>

      {/* Sellers List */}
      {Object.entries(groupedOrders).map(([sellerKey, group]) => {
        const isExpanded = expandedSellers[sellerKey];
        const allOrders = group.orders;

        // No longer filtered — the reconciliation dashboard shows the JMI
        // cleared/due state per order.
        const filteredOrders = allOrders;

        if (filteredOrders.length === 0) {
          return null;
        }

        const totals = calculateTotals(allOrders);

        return (
          <div
            key={sellerKey}
            style={{
              marginBottom: "16px",
              border: "2px solid #1f2937",
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: "#ffffff",
            }}
          >
            {/* Seller Summary Header */}
            <div
              onClick={() => toggleSeller(sellerKey)}
              style={{
                backgroundColor: "#1f2937",
                color: "#ffffff",
                padding: "10px 14px",
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#374151")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1f2937")}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <strong style={{ fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <span>{isExpanded ? "▼" : "▶"}</span>
                  Seller: {sellerKey}
                </strong>
                <span style={{ color: "#9ca3af" }}>·</span>
                <span>Orders: <strong>{totals.totalOrders}</strong></span>
                <span>Net: <strong>{totals.totalNetWt.toFixed(3)}g</strong></span>
                <span>Gross: <strong>{totals.totalGrossWt.toFixed(3)}g</strong></span>
                <span>Total Amount: <strong>₹{totals.totalAmount.toFixed(2)}</strong></span>
                <span style={{ color: "#9ca3af" }}>·</span>
                <span style={{ color: "#fbbf24" }}>
                  JMI Fine: <strong>{totals.jmiFine.toFixed(3)}g</strong>
                </span>
                <span style={{ color: "#86efac" }}>
                  JMI Comm: <strong>₹{totals.totalJmiComm.toFixed(2)}</strong>
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {totals.jmiPendingCount > 0 ? (
                  <span
                    style={{
                      backgroundColor: "#fef3c7",
                      color: "#92400e",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    ⏳ {totals.jmiPendingCount} Pending Friday Clearance
                  </span>
                ) : (
                  <span
                    style={{
                      backgroundColor: "#dcfce7",
                      color: "#166534",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                  >
                    ✅ All JMI Cleared
                  </span>
                )}
              </div>
            </div>

            {/* Expanded Table Content */}
            {isExpanded && (
              <div>
                <TableContent
                  orders={filteredOrders}
                  rtgsSaleRate={rtgsSaleRate}
                  formatDate={formatDate}
                  getOrderStatusBadge={getOrderStatusBadge}
                  onViewFourPartyPricing={(order) => setSelectedPricingOrder(order)}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* Local Seller Settlement Modal */}
      {modalOrders && (
        <JmiCommissionPayModal
          orders={modalOrders}
          rtgsSaleRate={rtgsSaleRate}
          totalJmiFine={calculateJmiSettlement(modalOrders).fine}
          totalJmiAmount={calculateJmiSettlement(modalOrders).amount}
          onClose={() => setModalOrders(null)}
          onSuccess={(msg) => {
            setAlertMessage(msg);
            setModalOrders(null);
          }}
        />
      )}

      {/* 4-Party Pricing Breakdown Modal */}
      {selectedPricingOrder && (
        <FourPartyPricingModal
          order={selectedPricingOrder}
          rtgsSaleRate={rtgsSaleRate}
          onClose={() => setSelectedPricingOrder(null)}
        />
      )}
    </div>
  );
};

// Helper component for delivered orders table
const TableContent = ({
  orders,
  rtgsSaleRate,
  formatDate,
  getOrderStatusBadge,
  onViewFourPartyPricing,
}) => {
  const [expandedOrderRows, setExpandedOrderRows] = useState({});

  const toggleOrderPricing = (orderId) => {
    setExpandedOrderRows((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  if (orders.length === 0) {
    return (
      <div style={{ padding: "16px", textAlign: "center", color: "#6b7280", fontSize: "13px" }}>
        No orders match the selected filter.
      </div>
    );
  }

  const rate = parseFloat(rtgsSaleRate) || 0;

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ minWidth: "100%", borderCollapse: "collapse", border: "1px solid #1f2937" }}>
        <thead>
          <tr>
            <th style={tableHeaderStyle}></th>
            <th style={tableHeaderStyle}>Image</th>
            <th style={tableHeaderStyle}>Order ID</th>
            <th style={tableHeaderStyle}>Product</th>
            <th style={tableHeaderStyle}>Status</th>
            <th style={tableHeaderStyle}>Qty</th>
            <th style={tableHeaderStyle}>Net Wt (g)</th>
            <th style={tableHeaderStyle}>Amount (₹)</th>
            <th style={{ ...tableHeaderStyle, backgroundColor: "#fef3c7", color: "#92400e" }}>
              JMI Fine (g)
            </th>
            <th style={{ ...tableHeaderStyle, backgroundColor: "#fef3c7", color: "#92400e" }}>
              JMI Fine Amt (Fine × Rate)
            </th>
            <th style={{ ...tableHeaderStyle, backgroundColor: "#fef3c7", color: "#92400e" }}>
              JMI MC (₹)
            </th>
            <th style={{ ...tableHeaderStyle, backgroundColor: "#eef2ff", color: "#3730a3" }}>
              Total JMI Comm (₹)
            </th>
            <th style={tableHeaderStyle}>4-Pricing</th>
            <th style={tableHeaderStyle}>JMI Clearance</th>
            <th style={tableHeaderStyle}>Order Time</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order, index) => {
            const pricing = calculateFourPartyPricing(order, rate);
            const { buyer, seller, operator, jmi } = pricing;
            const orderIdKey = order.docId || order.id || index;
            const isRowExpanded = expandedOrderRows[orderIdKey];
            const isJmiCleared = Boolean(order.jmiSettled || order.jmiCommissionSettled);

            return (
              <React.Fragment key={orderIdKey}>
                <tr
                  style={{
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9fafb",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <td style={{ ...tableCellStyle, textAlign: "center", width: "24px" }}>
                    <button
                      onClick={() => toggleOrderPricing(orderIdKey)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#4b5563",
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                      title="Toggle 4-Party Pricing Details"
                    >
                      {isRowExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: "center" }}>
                    {order.images?.[0]?.url ? (
                      <img
                        src={order.images[0].url.trim()}
                        alt="Product"
                        style={{
                          height: "28px",
                          width: "28px",
                          objectFit: "contain",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                        }}
                      />
                    ) : (
                      <span style={{ color: "#9ca3af" }}>—</span>
                    )}
                  </td>
                  <td style={{ ...tableCellStyle, fontFamily: "monospace", fontSize: "11px", fontWeight: "600" }}>
                    {order.id || order.docId || "—"}
                  </td>
                  <td style={{ ...tableCellStyle, fontWeight: "bold", fontSize: "12px" }}>
                    {order.productName || "—"}
                  </td>
                  <td style={tableCellStyle}>{getOrderStatusBadge(order.orderStatus)}</td>
                  <td style={tableCellStyle}>{parseFloat(order.totalQty || 1).toFixed(2)}</td>
                  <td style={tableCellStyle}>{parseFloat(order.netWt || pricing.weights.netWt).toFixed(3)}</td>
                  <td style={tableCellStyle}>
                    ₹{parseFloat(order.totalAmount || order.totalPaymentAmount || seller.totalAmount).toFixed(2)}
                  </td>

                  {/* JMI Financial Columns */}
                  <td style={{ ...tableCellStyle, backgroundColor: "#fffbeb", fontWeight: "600" }}>
                    {jmi.fineWeight.toFixed(3)}g
                  </td>
                  <td style={{ ...tableCellStyle, backgroundColor: "#fffbeb", color: "#059669", fontWeight: "600" }}>
                    ₹{jmi.fineAmount.toFixed(2)}
                  </td>
                  <td style={{ ...tableCellStyle, backgroundColor: "#fffbeb" }}>
                    ₹{jmi.totalMc.toFixed(2)}
                  </td>
                  <td style={{ ...tableCellStyle, backgroundColor: "#f5f3ff", color: "#4338ca", fontWeight: "700" }}>
                    ₹{jmi.totalCommission.toFixed(2)}
                  </td>

                  {/* 4-Party Pricing Breakdown Button */}
                  <td style={tableCellStyle}>
                    <button
                      onClick={() => onViewFourPartyPricing(order)}
                      style={{
                        padding: "3px 8px",
                        borderRadius: "4px",
                        backgroundColor: "#e0e7ff",
                        color: "#3730a3",
                        border: "1px solid #c7d2fe",
                        fontSize: "11px",
                        fontWeight: "600",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <Calculator size={11} /> 4 Pricing
                    </button>
                  </td>

                  {/* JMI Clearance Badge */}
                  <td style={tableCellStyle}>
                    {isJmiCleared ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            backgroundColor: "#dcfce7",
                            color: "#166534",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "bold",
                          }}
                        >
                          <CheckCircle2 size={11} /> Cleared
                        </span>
                        {order.jmiUtrNumber && (
                          <span style={{ fontSize: "10px", color: "#4b5563", fontFamily: "monospace" }}>
                            UTR: {order.jmiUtrNumber}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          backgroundColor: "#fef3c7",
                          color: "#92400e",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          fontWeight: "bold",
                        }}
                      >
                        <Clock size={11} /> Friday Due
                      </span>
                    )}
                  </td>

                  <td style={{ ...tableCellStyle, fontSize: "11px", color: "#4b5563" }}>
                    {formatDate(order.orderTime || order.addedAt)}
                  </td>
                </tr>

                {/* Inline 4-Party Pricing Drawer */}
                {isRowExpanded && (
                  <tr>
                    <td colSpan={15} style={{ backgroundColor: "#f8fafc", padding: "12px 16px", border: "1px solid #cbd5e1" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "10px" }}>
                        {/* 1. Buyer */}
                        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "6px", padding: "8px 10px" }}>
                          <div style={{ fontWeight: "700", color: "#1e40af", fontSize: "12px", marginBottom: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                            <User size={13} /> 1. Buyer Pricing
                          </div>
                          <div style={{ fontSize: "11px", color: "#334155" }}>
                            <div>Fine: <strong>{buyer.finePercentage.toFixed(2)}%</strong> ({buyer.fineWeight.toFixed(3)}g)</div>
                            <div>Making Charges: <strong>₹{buyer.totalMc.toFixed(2)}</strong></div>
                          </div>
                        </div>

                        {/* 2. Seller */}
                        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "6px", padding: "8px 10px" }}>
                          <div style={{ fontWeight: "700", color: "#92400e", fontSize: "12px", marginBottom: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                            <Store size={13} /> 2. Seller Pricing
                          </div>
                          <div style={{ fontSize: "11px", color: "#334155" }}>
                            <div>Fine: <strong>{seller.finePercentage.toFixed(2)}%</strong> ({seller.fineWeight.toFixed(3)}g)</div>
                            <div>Making Charges: <strong>₹{seller.totalMc.toFixed(2)}</strong></div>
                          </div>
                        </div>

                        {/* 3. Operator */}
                        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "6px", padding: "8px 10px" }}>
                          <div style={{ fontWeight: "700", color: "#166534", fontSize: "12px", marginBottom: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                            <UserCheck size={13} /> 3. Operator Pricing
                          </div>
                          <div style={{ fontSize: "11px", color: "#334155" }}>
                            <div>Fine: <strong>{operator.finePercentage.toFixed(2)}%</strong> ({operator.fineWeight.toFixed(3)}g)</div>
                            <div>Making Charges: <strong>₹{operator.totalMc.toFixed(2)}</strong></div>
                          </div>
                        </div>

                        {/* 4. JMI */}
                        <div style={{ background: "#faf5ff", border: "1px solid #ddd6fe", borderRadius: "6px", padding: "8px 10px" }}>
                          <div style={{ fontWeight: "700", color: "#5b21b6", fontSize: "12px", marginBottom: "4px", display: "flex", alignItems: "center", gap: "4px" }}>
                            <Landmark size={13} /> 4. JMI Commission
                          </div>
                          <div style={{ fontSize: "11px", color: "#334155" }}>
                            <div>Fine: <strong>{jmi.finePercentage.toFixed(2)}%</strong> ({jmi.fineWeight.toFixed(3)}g = ₹{jmi.fineAmount.toFixed(2)})</div>
                            <div>MC: <strong>₹{jmi.totalMc.toFixed(2)}</strong></div>
                            <div style={{ color: "#4338ca", fontWeight: "700", marginTop: "2px" }}>
                              Total Comm: ₹{jmi.totalCommission.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PaymentDeliveredList;
